import { useEffect, useState } from "react";
import "@/App.css";
import {
  ArrowRight,
  QrCode,
  Plus,
  Minus,
  Lock,
  Eye,
  ShieldCheck,
  Sparkles,
  Infinity as InfinityIcon,
  Briefcase,
  Music,
  Trophy,
  Share2,
  CalendarDays,
  ShoppingBag,
  User,
  Fingerprint,
  Link2,
  BookOpen,
  Gem,
  Plus as PlusIcon,
  Pencil,
  Check,
} from "lucide-react";
import {
  translations,
  shirtImages,
  shirtBack,
  cwLogo,
  certificateImage,
  showcaseEventsProfile,
  showcaseAdminScreen,
} from "@/translations";
import PreorderPopup, { OPEN_EVENT } from "@/PreorderPopup";

const GOLD = "#c8a268";

const openPreorder = () =>
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));

const scrollToMarketing = () => {
  const el = document.getElementById("marketing");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SHOP_URL =
  "https://use1id-prd.vercel.app/r/logon?next=%2Fr%2Fcompany-apps%2FWEBSHOP%2Fworkspaces%2FCONNECTED_WORDS%2Fshop";

const openShop = () => {
  window.open(SHOP_URL, "_blank", "noopener,noreferrer");
};

function useLanguage() {
  // Default = NL voor alle bezoekers. De taalkeuze wordt per sessie
  // onthouden via component-state maar niet persistent opgeslagen,
  // zodat een nieuwe pagina-opening altijd in NL start (incl. NL popup).
  const [lang, setLang] = useState("nl");
  useEffect(() => {
    try {
      document.documentElement.setAttribute("lang", lang);
    } catch (e) {
      /* noop */
    }
  }, [lang]);
  return [lang, setLang];
}

const Logo = ({ size = 36 }) => (
  <img
    src={cwLogo}
    alt="Connected Words logo"
    width={size}
    height={size}
    className="object-contain"
    style={{ height: size, width: size }}
  />
);

const Header = ({ lang, setLang, onReserve }) => {
  const t = translations[lang];
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/70 border-b border-white/5"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 h-[88px] flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3">
          <Logo size={56} />
          <span className="text-[22px] md:text-[26px] tracking-normal leading-none">
            <span style={{ color: GOLD, fontStyle: "italic" }} className="font-serif">
              Connected
            </span>{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }} className="font-serif">
              Words
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
          {[
            ["#limited", t.nav.limited],
            ["#unique", t.nav.unique],
            ["#story", t.nav.story],
            ["#connected", t.nav.connected],
            ["#launch", t.nav.launch],
            ["#magic", t.nav.magic],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-white/70 hover:text-white text-[11px] tracking-[0.22em] uppercase transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            data-testid="language-toggle"
            className="flex items-center text-[11px] tracking-[0.18em] border border-white/15 rounded-sm overflow-hidden"
          >
            <button
              data-testid="lang-nl"
              onClick={() => setLang("nl")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "nl" ? "text-black" : "text-white/70 hover:text-white"
              }`}
              style={lang === "nl" ? { backgroundColor: GOLD } : {}}
            >
              NL
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "en" ? "text-black" : "text-white/70 hover:text-white"
              }`}
              style={lang === "en" ? { backgroundColor: GOLD } : {}}
            >
              EN
            </button>
          </div>
          <button
            data-testid="reserve-top-btn"
            onClick={onReserve}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 border border-white/30 text-white/90 hover:bg-white hover:text-black text-[11px] tracking-[0.22em] uppercase transition-colors"
          >
            {t.cta.reserveTop}
          </button>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase">
          <div className="flex items-center gap-3 text-white/45">
            <span>{t.breadcrumb.edition}</span>
            <span className="text-white/20">/</span>
            <span>{t.breadcrumb.title}</span>
            <span className="text-white/20">/</span>
            <span>{t.breadcrumb.drop}</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: GOLD, opacity: 0.6 }}
              />
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ backgroundColor: GOLD }}
              />
            </span>
            {t.breadcrumb.live}
          </div>
        </div>
      </div>
    </header>
  );
};

const Hero = ({ lang, onReserve }) => {
  const t = translations[lang];
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-[140px] pb-20 overflow-hidden"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 70% 40%, rgba(200,162,104,0.07), transparent 60%), radial-gradient(ellipse at 30% 80%, rgba(255,255,255,0.025), transparent 60%)",
        }}
      />
      <div
        data-testid="side-label"
        className="hidden xl:flex absolute top-[140px] right-6 items-center gap-3 border border-white/15 px-3 py-2 text-[9px] tracking-[0.22em] uppercase text-white/60 max-w-[220px] leading-[1.5]"
      >
        {t.sideLabel}
      </div>

      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-6 items-center relative">
        <div className="relative z-10">
          <div className="text-[10px] tracking-[0.36em] uppercase mb-8" style={{ color: GOLD }}>
            {t.hero.eyebrow}
          </div>
          <h1
            data-testid="hero-headline"
            className="font-serif text-white text-[60px] md:text-[84px] xl:text-[100px] leading-[0.95] tracking-[-0.02em]"
          >
            {t.hero.headlineParts[0]}
            <span style={{ color: GOLD, fontStyle: "italic" }}>{t.hero.headlineParts[1]}</span>
            {t.hero.headlineParts[2]}
            <br />
            <span style={{ color: GOLD, fontStyle: "italic" }}>
              {t.hero.headlineParts[3].trim()}
            </span>{" "}
            <span>{t.hero.headlineParts[4]}</span>
          </h1>
          <p className="mt-6 font-serif italic text-[22px] md:text-[26px] text-white/90 max-w-md leading-[1.3]">
            {t.hero.sub}
          </p>
          <p className="mt-5 text-white/55 text-[14px] leading-[1.75] max-w-md">{t.hero.intro}</p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-reserve-btn"
              onClick={onReserve}
              className="group inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase text-black transition-all hover:gap-4"
              style={{ backgroundColor: GOLD }}
            >
              {t.cta.reserveHero}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <a
              data-testid="hero-read-concept-btn"
              href="#limited"
              className="inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase border border-white/25 text-white/90 hover:border-white hover:bg-white/5 transition-all"
            >
              {t.cta.readConcept}
              <QrCode size={16} />
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-white/10 pt-7">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                {t.hero.stats.edition}
              </div>
              <div className="font-serif text-[28px] text-white mt-1">
                {t.hero.stats.editionValue}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                {t.hero.stats.words}
              </div>
              <div className="font-serif text-[28px] text-white mt-1">
                {t.hero.stats.wordsValue}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                {t.hero.stats.reprint}
              </div>
              <div className="font-serif text-[28px] mt-1" style={{ color: GOLD }}>
                {t.hero.stats.reprintValue}
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-[520px] md:h-[640px]">
          <div
            data-testid="hero-shirt-front"
            className="absolute top-0 right-0 w-[78%] aspect-square"
            style={{ animation: "cw-float 8s ease-in-out infinite" }}
          >
            <img
              src={shirtImages[5]}
              alt="MEANING t-shirt — front"
              className="w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              loading="eager"
            />
          </div>
          <div
            data-testid="hero-shirt-back"
            className="absolute bottom-0 left-[2%] w-[55%] aspect-square"
            style={{ animation: "cw-float 8s ease-in-out -4s infinite" }}
          >
            <img
              src={shirtBack}
              alt="Shirt back — unique QR code"
              className="w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              loading="eager"
            />
            <div className="absolute -bottom-2 left-2 right-2 text-center text-[9px] tracking-[0.3em] uppercase text-white/50">
              {translations[lang].serialOf(42)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* 01 · LIMITED */
const SectionLimited = ({ lang }) => {
  const t = translations[lang].limited;
  return (
    <section id="limited" data-testid="section-limited" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            {t.number}
          </div>
          <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-white/40">{t.eyebrow}</div>
          <h2 className="mt-6 font-serif text-white text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]">
            {t.title}
          </h2>
          <p className="mt-7 text-white/65 text-[16px] leading-[1.8] max-w-md">{t.body}</p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {t.stats.map((s) => (
              <div key={s.label} data-testid={`limited-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-[#0a0a0b] p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{s.label}</div>
                <div className="mt-3 font-serif text-white text-[36px] leading-none">{s.value}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 border-t border-white/10 pt-8">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.whyItMatters}
            </div>
            <ul className="mt-5 space-y-3">
              {t.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80 text-[15px] leading-[1.6]">
                  <span className="mt-2 inline-block w-1 h-1" style={{ backgroundColor: GOLD }} />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const WordTile = ({ idx, total, word, meaning, image, lang }) => {
  const [open, setOpen] = useState(false);
  const t = translations[lang];
  return (
    <div
      data-testid={`word-tile-${word.toLowerCase()}`}
      className="group relative border border-white/8 bg-white/[0.015] hover:bg-white/[0.03] transition-colors overflow-hidden"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className="aspect-[4/5] relative overflow-hidden">
        <img
          src={image}
          alt={`${word} t-shirt`}
          className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase text-white/60">
          {t.serialOf(idx === 5 ? 100 : (idx + 1) * 17)}
        </div>
        <div className="absolute top-4 right-4 text-[9px] tracking-[0.3em] uppercase text-white/60">
          {t.wordIndex(idx + 1, total)}
        </div>

        <div className="absolute left-5 right-5 bottom-5">
          <div className="flex items-end justify-between gap-4">
            <h3 className="font-serif text-white text-[32px] md:text-[40px] leading-none tracking-[-0.02em]">
              {word}
            </h3>
            <button
              data-testid={`word-toggle-${word.toLowerCase()}`}
              onClick={() => setOpen((o) => !o)}
              aria-label={`Toggle ${word} meaning`}
              className="w-9 h-9 rounded-full border border-white/30 text-white/80 hover:bg-white hover:text-black transition-colors flex items-center justify-center"
            >
              {open ? <Minus size={14} /> : <Plus size={14} />}
            </button>
          </div>
          <div className="mt-2 text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>
            {t.perWord}
          </div>
          <div
            className="overflow-hidden transition-all duration-500 ease-out"
            style={{ maxHeight: open ? 160 : 0, opacity: open ? 1 : 0 }}
          >
            <p className="mt-3 text-white/75 text-[13px] leading-[1.6] pr-10">{meaning}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* 01b · The Collection tegels */
const SectionCollection = ({ lang }) => {
  const t = translations[lang].collection;
  const words = translations[lang].words;
  return (
    <section id="collection" data-testid="section-collection" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <h2 className="mt-6 font-serif text-white text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]">
              {t.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-white/55 text-[14px] leading-[1.7]">{t.hint}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {words.map((w, i) => (
            <WordTile
              key={w.word}
              idx={i}
              total={words.length}
              word={w.word}
              meaning={w.meaning}
              image={shirtImages[i]}
              lang={lang}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* 02 · UNIQUE — Your number · Your QR · Your profile */
const SectionUnique = ({ lang }) => {
  const t = translations[lang].unique;
  return (
    <section id="unique" data-testid="section-unique" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            {t.number}
          </div>
          <h2 className="mt-6 font-serif text-white text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]">
            {t.title}
          </h2>
          <p className="mt-7 text-white/65 text-[16px] leading-[1.8] max-w-md">{t.body}</p>

          <div className="mt-10 relative aspect-square max-w-[440px]">
            <img
              src={shirtBack}
              alt="Shirt back with serial number and unique QR code"
              className="w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              loading="lazy"
            />
            <div className="absolute bottom-2 left-2 text-[10px] tracking-[0.3em] uppercase text-white/50">
              E1-42/100
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {t.pillars.map((p, i) => (
              <div
                key={p.label}
                data-testid={`unique-pillar-${i}`}
                className="bg-[#0a0a0b] p-6 hover:bg-white/[0.02] transition-colors flex flex-col"
              >
                <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: GOLD }}>
                  {p.label}
                </div>
                <ul className="mt-5 space-y-2.5">
                  {p.items.map((it, j) => (
                    <li key={j} className="text-white/70 text-[13px] leading-[1.55] flex items-start gap-2">
                      <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 border p-6" style={{ borderColor: "rgba(200,162,104,0.35)", background: "linear-gradient(180deg, rgba(200,162,104,0.06), rgba(200,162,104,0.02))" }}>
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.authenticityLabel}
            </div>
            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
              {t.authenticity.map((a, i) => (
                <li key={i} className="text-white/85 text-[13.5px] leading-[1.55] flex items-start gap-2">
                  <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* 03 · YOUR STORY — What can your shirt reveal? (7 category tiles) */
const STORY_ICONS = {
  BUSINESS: Briefcase,
  MUSIC: Music,
  SPORT: Trophy,
  "SOCIAL / CREATOR": Share2,
  EVENTS: CalendarDays,
  SHOP: ShoppingBag,
  YOU: User,
};

const SectionStory = ({ lang }) => {
  const t = translations[lang].story;
  return (
    <section id="story" data-testid="section-story" className="relative py-28 border-t border-white/5 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(200,162,104,0.06), transparent 55%)",
        }}
      />
      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.36em] uppercase text-white/40">
              {t.eyebrow}
            </div>
            <h2 className="mt-6 font-serif text-white text-[44px] md:text-[64px] xl:text-[76px] leading-[1.0] tracking-[-0.02em]">
              {t.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/65 text-[15px] leading-[1.8]">{t.body}</p>
          </div>
        </div>

        <div
          data-testid="story-category-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          {t.categories.map((c, i) => {
            const Icon = STORY_ICONS[c.key] || Sparkles;
            return (
              <div
                key={c.key}
                data-testid={`story-cat-${c.key.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="group bg-[#0a0a0b] hover:bg-white/[0.025] transition-colors p-7 flex flex-col min-h-[280px]"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="w-11 h-11 flex items-center justify-center border border-white/15 shrink-0"
                    style={{ color: GOLD }}
                  >
                    <Icon size={18} />
                  </div>
                  <span className="text-[10px] tracking-[0.28em] uppercase text-white/30">
                    0{i + 1}/07
                  </span>
                </div>
                <div className="mt-6 font-serif text-white text-[24px] leading-[1.1] tracking-[-0.01em]">
                  {c.key}
                </div>
                <ul className="mt-4 space-y-1.5">
                  {c.items.map((it, j) => (
                    <li key={j} className="text-white/55 text-[12.5px] leading-[1.55] flex items-start gap-2">
                      <span className="mt-[7px] inline-block w-[3px] h-[3px] shrink-0" style={{ backgroundColor: GOLD }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {/* 8e tegel = core message */}
          <div
            data-testid="story-core-message"
            className="bg-[#0a0a0b] p-7 flex flex-col justify-center"
            style={{
              background:
                "linear-gradient(180deg, rgba(200,162,104,0.10), rgba(200,162,104,0.02))",
            }}
          >
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              CORE
            </div>
            <div className="mt-5 font-serif text-white text-[28px] md:text-[32px] leading-[1.05] tracking-[-0.01em]">
              {t.coreLine1}
            </div>
            <div className="mt-2 font-serif italic text-[22px] md:text-[26px] leading-[1.15]" style={{ color: GOLD }}>
              {t.coreLine2}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* 04 · LIVING — Same shirt. New story. */
const SectionLiving = ({ lang }) => {
  const t = translations[lang].living;
  return (
    <section id="living" data-testid="section-living" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.36em] uppercase text-white/40">
              {t.eyebrow}
            </div>
            <h2 className="mt-6 font-serif text-white text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]">
              {t.title}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-white/65 text-[16px] leading-[1.8]">{t.body}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-14">
          {t.pivots.map((p, i) => (
            <div
              key={p.label}
              data-testid={`living-pivot-${i}`}
              className="bg-[#0a0a0b] p-8 min-h-[220px] flex flex-col justify-between hover:bg-white/[0.025] transition-colors"
            >
              <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
                {p.label}
              </div>
              <div className="font-serif text-white text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.01em]">
                {p.body}
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 border p-8" style={{ borderColor: "rgba(200,162,104,0.35)" }}>
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              CORE IDEA
            </div>
            {t.coreIdea.map((line, i) => (
              <div
                key={i}
                className="mt-4 font-serif text-white text-[24px] md:text-[28px] leading-[1.15] tracking-[-0.01em]"
              >
                {line}
              </div>
            ))}
          </div>
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.ownershipTitle}
            </div>
            <ul className="mt-5 space-y-3">
              {t.ownership.map((o, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80 text-[14.5px] leading-[1.6]">
                  <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* 05 · CONNECTED — See → Notice → Scan → Discover → Connect */
const SectionConnected = ({ lang }) => {
  const t = translations[lang].connected;
  return (
    <section id="connected" data-testid="section-connected" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.36em] uppercase text-white/40">
              {t.eyebrow}
            </div>
            <h2 className="mt-6 font-serif text-white text-[44px] md:text-[60px] leading-[1.02] tracking-[-0.02em]">
              {t.title}
            </h2>
          </div>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-14">
          {t.steps.map((s, i) => (
            <li
              key={i}
              data-testid={`connected-step-${i + 1}`}
              className="group relative bg-[#0a0a0b] hover:bg-white/[0.02] transition-colors p-7 min-h-[260px] flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-[44px] leading-none" style={{ color: GOLD }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-white/30">
                  STEP {i + 1}/5
                </span>
              </div>
              <div className="mt-8 font-serif text-white text-[22px] leading-[1.15] tracking-[-0.01em]">
                {s.title}
              </div>
              <p className="mt-3 text-white/55 text-[13px] leading-[1.7]">{s.body}</p>
              <div
                className="absolute left-0 right-0 bottom-0 h-px scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"
                style={{ backgroundColor: GOLD }}
              />
            </li>
          ))}
        </ol>

        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.possibilitiesLabel}
            </div>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {t.possibilities.map((p, i) => (
                <li key={i} className="text-white/80 text-[13.5px] leading-[1.55] flex items-start gap-2">
                  <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.futureLabel}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {t.future.map((f) => (
                <span key={f} className="text-[11px] tracking-[0.28em] uppercase text-white/75 border border-white/15 px-3 py-1.5">
                  {f}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Launch concept banner */}
        <div
          data-testid="connected-launch-concept"
          className="border p-8 md:p-10 text-center"
          style={{ borderColor: "rgba(200,162,104,0.35)", background: "linear-gradient(180deg, rgba(200,162,104,0.08), rgba(200,162,104,0.02))" }}
        >
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            LAUNCH CONCEPT
          </div>
          <div className="mt-4 font-serif text-white text-[22px] md:text-[32px] leading-[1.2] tracking-[-0.01em]">
            {t.launchConcept}
          </div>
        </div>

        {/* Woord-shirts rij */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {shirtImages.map((src, i) => {
            const word = translations[lang].words[i].word;
            return (
              <div
                key={word}
                data-testid={`connected-shirt-${word.toLowerCase()}`}
                className="group relative aspect-[3/4] bg-white/[0.015] border border-white/8 overflow-hidden"
              >
                <img
                  src={src}
                  alt={`${word} shirt`}
                  className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute top-3 left-3 text-[9px] tracking-[0.3em] uppercase text-white/60">
                  {String(i + 1).padStart(2, "0")} / 06
                </div>
                <div className="absolute left-3 right-3 bottom-3">
                  <div className="font-serif text-white text-[20px] md:text-[26px] leading-none tracking-[-0.01em]">
                    {word}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* 06 · PHYSICAL PRODUCT — Certificate + components in the box */
const SectionPhysical = ({ lang }) => {
  const t = translations[lang].physical;
  return (
    <section id="physical" data-testid="section-physical" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div
              data-testid="certificate-photo"
              className="relative overflow-hidden border border-white/10 bg-[#141311] select-none"
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <img
                src={certificateImage}
                alt="Connected Words · Certificate of Authenticity (specimen)"
                className="w-full h-auto object-contain pointer-events-none"
                loading="lazy"
                draggable="false"
              />
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
                <div
                  className="whitespace-nowrap font-serif italic"
                  style={{
                    transform: "rotate(-22deg)",
                    fontSize: "clamp(48px, 12vw, 160px)",
                    color: "rgba(200, 162, 104, 0.42)",
                    letterSpacing: "0.18em",
                    textShadow: "0 2px 12px rgba(0,0,0,0.35)",
                    lineHeight: 1,
                  }}
                >
                  SPECIMEN · SPECIMEN · SPECIMEN
                </div>
              </div>
              <div
                className="absolute inset-0 pointer-events-none opacity-25"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(-22deg, transparent 0 60px, rgba(200,162,104,0.06) 60px 61px)",
                  mixBlendMode: "screen",
                }}
              />
            </div>
            <p className="mt-3 text-white/40 text-[11px] leading-[1.6] italic">{t.specimenNote}</p>
          </div>

          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-white/40">
              {t.eyebrow}
            </div>
            <h2 className="mt-4 font-serif text-white text-[36px] md:text-[46px] leading-[1.05] tracking-[-0.02em]">
              {t.title}
            </h2>
            <p className="mt-6 text-white/65 text-[15px] leading-[1.8]">{t.body}</p>

            <div className="mt-8 border border-white/10 p-6">
              <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
                {t.componentsLabel}
              </div>
              <ul className="mt-4 grid grid-cols-1 gap-2">
                {t.components.map((c, i) => (
                  <li key={i} className="text-white/85 text-[13.5px] leading-[1.55] flex items-start gap-2">
                    <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="transfer-card"
              className="mt-8 border p-6"
              style={{
                borderColor: "rgba(200,162,104,0.35)",
                background:
                  "linear-gradient(180deg, rgba(200,162,104,0.07), rgba(200,162,104,0.02))",
              }}
            >
              <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
                1iD · Ownership Transfer
              </div>
              <div className="mt-3 font-serif text-white text-[24px] leading-[1.2]">
                {t.transferTitle}
              </div>
              <p className="mt-3 text-white/70 text-[13px] leading-[1.7]">{t.transferBody}</p>
            </div>

            <p className="mt-8 font-serif italic text-white/85 text-[18px] leading-[1.4]">
              {t.coreIdea}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* 07 · LAUNCH / FOMO */
const SectionLaunch = ({ lang, onReserve }) => {
  const t = translations[lang].launch;
  return (
    <section id="launch" data-testid="section-launch" className="relative py-28 border-t border-white/5 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(200,162,104,0.08), transparent 55%)",
        }}
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            {t.number}
          </div>
          <div className="mt-3 flex items-baseline gap-4">
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">
              {t.launchLabel}
            </span>
            <span className="font-serif text-white text-[26px] md:text-[36px] tracking-[-0.01em]">
              {t.launchDate}
            </span>
          </div>
          <h2 className="mt-6 font-serif text-white text-[48px] md:text-[72px] leading-[0.98] tracking-[-0.02em]">
            {t.title}
          </h2>
          <p className="mt-8 text-white/65 text-[16px] leading-[1.8] max-w-xl">{t.body}</p>

          <div className="mt-10 flex flex-wrap gap-2">
            {t.facts.map((f) => (
              <span
                key={f}
                className="text-[10px] tracking-[0.32em] uppercase border px-3 py-1.5"
                style={{ color: GOLD, borderColor: "rgba(200,162,104,0.4)" }}
              >
                {f}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-white/10 p-8 bg-white/[0.015]">
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: GOLD }}>
              {t.ctaTitle}
            </div>
            <ol className="mt-6 space-y-4">
              {t.ctaSteps.map((s, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-4 items-baseline">
                  <span className="font-serif text-[16px]" style={{ color: GOLD }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white text-[15px] leading-[1.5]">{s}</span>
                </li>
              ))}
            </ol>
            <button
              data-testid="launch-reserve-btn"
              onClick={onReserve}
              className="mt-8 w-full group inline-flex items-center justify-between gap-3 px-6 py-4 text-[11px] tracking-[0.22em] uppercase text-black transition-all"
              style={{ backgroundColor: GOLD }}
            >
              {translations[lang].cta.reserveOn1iD}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="mt-5 text-white/45 text-[12px] leading-[1.6]">{t.caption}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = ({ lang }) => {
  const text = translations[lang].marquee;
  const line = Array.from({ length: 6 }).map(() => text).join("   ·   ");
  return (
    <div data-testid="marquee" className="relative border-y border-white/10 py-6 overflow-hidden">
      <div className="flex whitespace-nowrap" style={{ animation: "cw-marquee 38s linear infinite" }}>
        <span
          className="font-serif text-[40px] md:text-[60px] leading-none tracking-[-0.02em] pr-10"
          style={{ color: GOLD, opacity: 0.85 }}
        >
          {line}
        </span>
        <span
          aria-hidden
          className="font-serif text-[40px] md:text-[60px] leading-none tracking-[-0.02em] pr-10"
          style={{ color: GOLD, opacity: 0.85 }}
        >
          {line}
        </span>
      </div>
    </div>
  );
};

/* 08 · THE MAGIC BEHIND IT — Powered by 1iD + personas */
const SectionMagic = ({ lang }) => {
  const t = translations[lang].magic;
  const personas = translations[lang].personas;
  const icons = [InfinityIcon, ShieldCheck, Eye, Lock, Sparkles, ShieldCheck, InfinityIcon];
  return (
    <section id="magic" data-testid="section-magic" className="relative py-28 border-t border-white/5">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
              {t.number}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.36em] uppercase text-white/40">
              {t.eyebrow}
            </div>
            <h2 className="mt-6 font-serif text-white text-[40px] md:text-[54px] leading-[1.05] tracking-[-0.02em]">
              {t.title}
            </h2>
            <p className="mt-7 text-white/65 text-[16px] leading-[1.8] max-w-md">{t.body}</p>
          </div>

          <div className="lg:col-span-6">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {t.bullets.map((b, i) => {
                const Icon = icons[i] || ShieldCheck;
                return (
                  <li
                    key={i}
                    data-testid={`magic-bullet-${i}`}
                    className="bg-[#0a0a0b] p-6 flex items-start gap-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="w-9 h-9 flex items-center justify-center border border-white/15 shrink-0" style={{ color: GOLD }}>
                      <Icon size={16} />
                    </div>
                    <div className="text-white/85 text-[13.5px] leading-[1.5]">{b}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div
          data-testid="magic-positioning"
          className="border p-8 text-center mb-16"
          style={{ borderColor: "rgba(200,162,104,0.35)", background: "linear-gradient(180deg, rgba(200,162,104,0.06), rgba(200,162,104,0.02))" }}
        >
          <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            {t.rulesLabel}
          </div>
          <div className="mt-5 font-serif text-white text-[24px] md:text-[32px] leading-[1.15] tracking-[-0.01em]">
            {t.rules[0]}
          </div>
          <div className="mt-2 font-serif italic text-[22px] md:text-[28px] leading-[1.2]" style={{ color: GOLD }}>
            {t.rules[1]}
          </div>
        </div>

        {/* Personas grid */}
        <div className="mb-10">
          <h3 className="font-serif text-white text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.01em]">
            {personas.title}
          </h3>
          <p className="mt-3 text-white/60 text-[15px] leading-[1.6] max-w-2xl">{personas.sub}</p>
        </div>

        <div data-testid="persona-grid" className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {personas.list.map((p, i) => (
            <div
              key={p.name}
              data-testid={`persona-card-${p.name.toLowerCase()}`}
              className="group relative bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 p-7 backdrop-blur-sm flex flex-col transition-all hover:border-white/25"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-white text-[34px] leading-none tracking-[-0.01em]">
                    {p.name}
                  </div>
                  <div
                    className="mt-2 inline-block text-[10px] tracking-[0.28em] uppercase border border-white/20 px-2 py-1"
                    style={{ color: GOLD }}
                  >
                    {p.type}
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                  0{i + 1} / 03
                </div>
              </div>

              <p className="mt-4 text-white/55 text-[13px] leading-[1.6] min-h-[42px]">
                {p.tagline}
              </p>

              <div className="mt-5 relative aspect-square bg-black flex items-center justify-center overflow-hidden p-3">
                <img
                  src={p.qr}
                  alt={`QR code · ${p.serial} · ${p.name}`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>

              <div className="mt-5 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase">
                <span className="text-white/55">{p.serial}</span>
                <span style={{ color: GOLD }}>1iD · MyProducts</span>
              </div>

              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`persona-simulate-${p.name.toLowerCase()}`}
                className="mt-6 group/btn inline-flex items-center justify-between gap-3 px-4 py-3 text-[10px] tracking-[0.28em] uppercase text-black transition-all hover:gap-4"
                style={{ backgroundColor: GOLD }}
              >
                <span className="flex items-center gap-2">
                  <QrCode size={14} />
                  {personas.simulate}
                </span>
                <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-0.5" />
              </a>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-white/45 text-[12px] leading-[1.6]">{personas.cardCaption}</p>
          <div
            className="inline-flex items-center gap-3 self-start md:self-auto text-[10px] tracking-[0.28em] uppercase border border-white/15 px-3 py-2"
            style={{ color: GOLD }}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: GOLD, opacity: 0.6 }}
              />
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ backgroundColor: GOLD }}
              />
            </span>
            {personas.cardStatus}
          </div>
        </div>
      </div>
    </section>
  );
};

/* Marketing showcase onderaan — end-to-end verhaal in 3 stappen + 6 pijlers.
   Structuur mirrort de originele "NOT JUST A T-SHIRT" marketingsplash,
   maar volledig HTML/CSS zodat de tekst NL/EN kan zijn. */
const SectionMarketing = ({ lang, onReserve }) => {
  const s = translations[lang].showcase;
  const pillarIcons = [Fingerprint, ShieldCheck, Link2, BookOpen, Gem, Lock];
  const manageIcons = [Pencil, PlusIcon, Eye, Share2, ShieldCheck];

  return (
    <section
      id="marketing"
      data-testid="section-marketing"
      className="relative py-28 border-t border-white/5 overflow-hidden"
    >
      {/* Golden ambient light */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 20%, rgba(200,162,104,0.12), transparent 55%), radial-gradient(ellipse at 50% 80%, rgba(200,162,104,0.06), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-10">
        {/* Top header row: 1iD (left) — headline (center) — CW (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] items-center gap-8 mb-14">
          <div className="flex items-center gap-2">
            <div
              className="font-serif font-bold text-[44px] leading-none"
              style={{ color: GOLD }}
            >
              1i<span className="text-white">D</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-white text-[13px] md:text-[15px] tracking-[0.32em] uppercase">
              {s.eyebrow}
            </div>
            <h2
              data-testid="showcase-title"
              className="mt-3 font-serif text-[38px] md:text-[56px] xl:text-[68px] leading-[1.02] tracking-[-0.02em]"
              style={{ color: GOLD }}
            >
              {s.title} {s.titleAccent}
            </h2>
            <div className="mt-4 text-white/85 text-[11px] md:text-[13px] tracking-[0.36em] uppercase">
              {s.sub}
            </div>
          </div>
          <div className="flex items-center gap-3 justify-self-end">
            <img src={cwLogo} alt="Connected Words" className="w-14 h-14 object-contain" />
            <div className="hidden md:block">
              <div className="font-serif italic text-[18px]" style={{ color: GOLD }}>
                Connected Words<sup className="text-[9px] not-italic">™</sup>
              </div>
              <div className="text-white/60 text-[9px] tracking-[0.28em] uppercase">
                {s.tagline}
              </div>
            </div>
          </div>
        </div>

        {/* 3-step showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Column 1 · CREATED TO BE UNIQUE */}
          <div data-testid="showcase-step-1" className="flex flex-col">
            <StepHeader idx={s.steps[0].index} title={s.steps[0].title} body={s.steps[0].body} accent={GOLD} />
            <div className="mt-8 relative flex-1 flex items-center justify-center min-h-[380px]">
              {/* Certificate photo (behind) */}
              <div className="absolute left-0 top-4 w-[46%] aspect-[3/4] border border-white/10 bg-[#141311] overflow-hidden shadow-2xl rotate-[-4deg]">
                <img
                  src={certificateImage}
                  alt="Certificate of Authenticity"
                  className="w-full h-full object-cover opacity-90"
                  loading="lazy"
                  draggable="false"
                />
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
                  <div
                    className="whitespace-nowrap font-serif italic"
                    style={{
                      transform: "rotate(-22deg)",
                      fontSize: "clamp(24px, 6vw, 60px)",
                      color: "rgba(200, 162, 104, 0.45)",
                      letterSpacing: "0.18em",
                    }}
                  >
                    SPECIMEN
                  </div>
                </div>
                <div className="absolute bottom-2 left-2 text-[8px] tracking-[0.3em] uppercase text-white/60">
                  E1 · 1/100
                </div>
              </div>
              {/* Shirt front */}
              <div className="absolute right-2 top-0 w-[64%] aspect-square">
                <img
                  src={shirtImages[0]}
                  alt="TOGETHER shirt — front"
                  className="w-full h-full object-contain drop-shadow-[0_25px_60px_rgba(200,162,104,0.25)]"
                  loading="lazy"
                />
              </div>
              {/* Shirt back with QR */}
              <div className="absolute right-0 bottom-0 w-[42%] aspect-square">
                <img
                  src={shirtBack}
                  alt="Shirt back with unique QR"
                  className="w-full h-full object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]"
                  loading="lazy"
                />
                <div className="absolute -bottom-1 left-2 text-[9px] tracking-[0.3em] uppercase text-white/70">
                  E1 · 100/100
                </div>
              </div>
            </div>
          </div>

          {/* Column 2 · SCAN TO CONNECT — events profile inside a phone frame */}
          <div data-testid="showcase-step-2" className="flex flex-col">
            <StepHeader idx={s.steps[1].index} title={s.steps[1].title} body={s.steps[1].body} accent={GOLD} />
            <div className="mt-8 relative flex-1 flex items-center justify-center min-h-[380px]">
              <PhoneFrame
                src={showcaseEventsProfile}
                alt="EVENTS ORGANIZORS — 1iD identity card"
              />
            </div>
          </div>

          {/* Column 3 · CONFIGURE & MANAGE — admin screen image + right-side actions */}
          <div data-testid="showcase-step-3" className="flex flex-col">
            <StepHeader idx={s.steps[2].index} title={s.steps[2].title} body={s.steps[2].body} accent={GOLD} />
            <div className="mt-8 grid grid-cols-[1fr_auto] gap-3 items-start">
              <img
                src={showcaseAdminScreen}
                alt="Configure & manage — QR Profiles admin screen"
                className="w-full max-w-[320px] h-auto object-contain"
                loading="lazy"
                draggable="false"
                style={{
                  filter: "drop-shadow(0 24px 44px rgba(200,162,104,0.18))",
                }}
              />
              <ul className="space-y-4 pt-2">
                {s.manageActions.map((a, i) => {
                  const Icon = manageIcons[i] || Check;
                  return (
                    <li
                      key={i}
                      data-testid={`showcase-manage-${i}`}
                      className="flex items-start gap-2 text-white/80 text-[11px] leading-[1.3] max-w-[130px]"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center shrink-0"
                        style={{ color: GOLD }}
                      >
                        <Icon size={14} />
                      </span>
                      <span>{a}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-16 h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(200,162,104,0.4), transparent)" }} />

        {/* 6 pillars row */}
        <div
          data-testid="showcase-pillars"
          className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8"
        >
          {s.pillars.map((p, i) => {
            const Icon = pillarIcons[i] || ShieldCheck;
            return (
              <div
                key={p.label}
                data-testid={`showcase-pillar-${p.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="text-center flex flex-col items-center"
              >
                <div
                  className="w-14 h-14 flex items-center justify-center border border-white/15 rounded-full mb-4"
                  style={{ color: GOLD }}
                >
                  <Icon size={22} />
                </div>
                <div className="font-serif text-white text-[16px] tracking-[0.14em] uppercase">
                  {p.label}
                </div>
                <p className="mt-2 text-white/60 text-[12px] leading-[1.5] max-w-[190px]">
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 flex justify-center">
          <button
            data-testid="showcase-cta-btn"
            onClick={onReserve}
            className="group inline-flex items-center gap-3 px-10 py-4 text-[11px] tracking-[0.28em] uppercase text-black transition-all hover:gap-5"
            style={{ backgroundColor: GOLD }}
          >
            {s.cta}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* Step header helper — number + title + subtitle */
const StepHeader = ({ idx, title, body, accent }) => (
  <div className="text-center">
    <div className="font-serif text-[26px] leading-none" style={{ color: accent }}>
      {idx}.
    </div>
    <div
      className="mt-2 font-serif text-[22px] md:text-[26px] tracking-[0.02em] uppercase"
      style={{ color: accent }}
    >
      {title}
    </div>
    <p className="mt-4 text-white/75 text-[13.5px] leading-[1.55] max-w-[290px] mx-auto">
      {body}
    </p>
  </div>
);

/* Phone frame — wraps an image inside a stylized phone body (notch + rounded corners) */
const PhoneFrame = ({ src, alt }) => (
  <div
    data-testid="showcase-phone-frame"
    className="relative w-[240px] md:w-[260px] mx-auto"
    style={{
      filter: "drop-shadow(0 30px 60px rgba(200,162,104,0.25))",
    }}
  >
    <div
      className="relative rounded-[36px] p-2 border border-white/10"
      style={{
        background: "linear-gradient(180deg, #1a1a20 0%, #0a0a0f 100%)",
        aspectRatio: "9/19",
      }}
    >
      {/* Notch */}
      <div className="absolute left-1/2 -translate-x-1/2 top-2 w-24 h-5 bg-black rounded-b-2xl z-10" />
      {/* Screen */}
      <div className="relative w-full h-full rounded-[28px] overflow-hidden bg-white">
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover object-top"
          loading="lazy"
          draggable="false"
        />
      </div>
    </div>
  </div>
);

const Footer = ({ lang }) => {
  const f = translations[lang].footer;
  return (
    <footer data-testid="footer" className="border-t border-white/10 py-10">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="flex items-center gap-3">
          <Logo size={42} />
          <span className="font-serif italic text-[18px]" style={{ color: GOLD }}>
            {f.tagline}
          </span>
        </div>
        <div className="text-white/40 text-[11px] tracking-[0.22em] uppercase">
          © {new Date().getFullYear()} · {f.rights}
        </div>
        <div className="flex items-center gap-6 text-white/50 text-[11px] tracking-[0.22em] uppercase">
          <button
            data-testid="footer-contact-btn"
            onClick={openPreorder}
            className="hover:text-white transition-colors text-left"
          >
            {f.contact}
          </button>
          <a href="#" className="hover:text-white transition-colors">{f.privacy}</a>
          <a href="#" className="hover:text-white transition-colors">{f.terms}</a>
        </div>
      </div>
    </footer>
  );
};

function App() {
  const [lang, setLang] = useLanguage();

  const onReserve = () => openShop();

  return (
    <div className="App min-h-screen bg-[#0a0a0b] text-white antialiased selection:bg-[#c8a268] selection:text-black">
      <Header lang={lang} setLang={setLang} onReserve={onReserve} />
      <main>
        <Hero lang={lang} onReserve={onReserve} />
        <SectionLimited lang={lang} />
        <SectionCollection lang={lang} />
        <SectionUnique lang={lang} />
        <SectionStory lang={lang} />
        <SectionLiving lang={lang} />
        <SectionConnected lang={lang} />
        <SectionPhysical lang={lang} />
        <SectionLaunch lang={lang} onReserve={onReserve} />
        <Marquee lang={lang} />
        <SectionMagic lang={lang} />
        <SectionMarketing lang={lang} onReserve={onReserve} />
      </main>
      <Footer lang={lang} />
      <PreorderPopup lang={lang} onReserve={onReserve} />
    </div>
  );
}

export default App;
